import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { stockService } from '../services/stock.service';

// Async thunks
export const fetchStocks = createAsyncThunk(
  'stock/fetchStocks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await stockService.getStocks();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks');
    }
  }
);

export const fetchActiveStocks = createAsyncThunk(
  'stock/fetchActiveStocks',
  async (shopId = null, { rejectWithValue }) => {
    try {
      if (shopId) {
        return await stockService.getStocksByShopId(shopId);
      }
      return await stockService.getActiveStocks();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks actifs');
    }
  }
);

export const fetchForSaleStocks = createAsyncThunk(
  'stock/fetchForSaleStocks',
  async (shopId = null, { rejectWithValue }) => {
    try {
      if (shopId) {
        return await stockService.getForSaleStocksByShopId(shopId);
      }
      return await stockService.getForSaleStocks();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks actifs');
    }
  }
);

export const fetchStockById = createAsyncThunk(
  'stock/fetchStockById',
  async (id, { rejectWithValue }) => {
    try {
      return await stockService.getStockById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération du stock');
    }
  }
);

export const fetchStocksByProductId = createAsyncThunk(
  'stock/fetchStocksByProductId',
  async (productId, { rejectWithValue }) => {
    try {
      return await stockService.getStocksByProductId(productId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks par produit');
    }
  }
);

export const fetchStocksByShopId = createAsyncThunk(
  'stock/fetchStocksByShopId',
  async (shopId, { rejectWithValue }) => {
    try {
      return await stockService.getStocksByShopId(shopId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks par magasin');
    }
  }
);

export const fetchStocksByInvnetoryId = createAsyncThunk(
  'stock/fetchStocksByInvnetoryId',
  async (inventoryId, { rejectWithValue }) => {
    try {
      return await stockService.getStocksByInventoryId(inventoryId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors de la récupération des stocks par inventaire'
      );
    }
  }
);

export const fetchExpiredStocks = createAsyncThunk(
  'stock/fetchExpiredStocks',
  async (_, { rejectWithValue }) => {
    try {
      return await stockService.getExpiredStocks();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks expirés');
    }
  }
);

export const addStock = createAsyncThunk(
  'stock/addStock',
  async (stock, { rejectWithValue }) => {
    try {
      const result = await stockService.createStock(stock);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création du stock');
    }
  }
);

export const addStockRange = createAsyncThunk(
  'stock/addStockRange',
  async (stocks, { rejectWithValue }) => {
    try {
      return await stockService.createStockRange(stocks);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création des stocks');
    }
  }
);

export const editStock = createAsyncThunk(
  'stock/editStock',
  async ({ id, stock }, { rejectWithValue }) => {
    try {
      await stockService.updateStock(id, stock);
      return { id, ...stock };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la mise à jour du stock');
    }
  }
);

export const removeStock = createAsyncThunk(
  'stock/removeStock',
  async (id, { rejectWithValue }) => {
    try {
      await stockService.deleteStock(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression du stock');
    }
  }
);

export const hardDeleteStock = createAsyncThunk(
  'stock/hardDeleteStock',
  async (id, { rejectWithValue }) => {
    try {
      await stockService.hardDeleteStock(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression définitive du stock');
    }
  }
);

export const createStockLine = createAsyncThunk(
  'stock/createStockLine',
  async (request, { rejectWithValue }) => {
    try {
      return await stockService.createStockLine(request);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création de la ligne de stock');
    }
  }
);

export const processStockMovement = createAsyncThunk(
  'stock/processStockMovement',
  async (request, { rejectWithValue }) => {
    try {
      return await stockService.processStockMovement(request);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors du traitement du mouvement de stock');
    }
  }
);

export const validateStockAvailability = createAsyncThunk(
  'stock/validateStockAvailability',
  async (request, { rejectWithValue }) => {
    try {
      return await stockService.validateStockAvailability(request);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la validation de la disponibilité');
    }
  }
);

// ICI: mode RabbitMQ + SignalR
export const saveInventoryRealQuantities = createAsyncThunk(
  'stock/saveInventoryRealQuantities',
  async ({ inventoryId, payload }, { rejectWithValue }) => {
    try {
      const result = await stockService.saveInventoryRealQuantities(inventoryId, payload);
      return result; // { jobId, inventoryId, status, message }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        'Erreur lors de la sauvegarde des quantités réelles'
      );
    }
  }
);

export const fetchTrashStocks = createAsyncThunk(
  'stock/fetchTrashStocks',
  async (_, { rejectWithValue }) => {
    try {
      return await stockService.getTrashStocks();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks corbeille');
    }
  }
);

export const fetchTransformStocks = createAsyncThunk(
  'stock/fetchTransformStocks',
  async (_, { rejectWithValue }) => {
    try {
      return await stockService.getTransformStocks();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des stocks transformés');
    }
  }
);

export const markStockToDestroy = createAsyncThunk(
  'stock/markStockToDestroy',
  async (id, { rejectWithValue }) => {
    try {
      const res = await stockService.markStockToDestroy(id);
      return { id, res };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors du marquage du stock à détruire'
      );
    }
  }
);

export const markStockToDestroyWithQte = createAsyncThunk(
  'stock/markStockToDestroyWithQte',
  async (payload, { rejectWithValue }) => {
    try {
     //  console.log("Stock Slice : markStockToDestroyWithQte payload", payload);
      const res = await stockService.markStockToDestroyWithQte(payload);
      return res;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors du marquage du stock à détruire'
      );
    }
  }
);

export const restoreStockFromDestruction = createAsyncThunk(
  'stock/restoreStockFromDestruction',
  async (id, { rejectWithValue }) => {
    try {
      const res = await stockService.restoreStockFromDestruction(id);
      return { id, res };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors de la restauration du stock'
      );
    }
  }
);

export const markStockToTransform = createAsyncThunk(
  'stock/markStockToTransform',
  async ({ id, quantity }, { rejectWithValue }) => {
    try {
      const res = await stockService.markStockToTransform(id, quantity);
      return { id, quantity, res };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors du marquage du stock à transformer'
      );
    }
  }
);

export const updateStockDateAndQuantity = createAsyncThunk(
  'stock/updateStockDateAndQuantity',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await stockService.updateStockDateAndQuantity(id, payload);
      return { id, payload, res };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors de la mise à jour de la date/quantité du stock'
      );
    }
  }
);

export const transformStockProduct = createAsyncThunk(
  'stock/transformStockProduct',
  async ({ id, request }, { rejectWithValue }) => {
    try {
      const res = await stockService.transformStockProduct(id, request);
      return { id, request, res };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors de la transformation de la ligne de stock'
      );
    }
  }
);

export const fetchNegativeStocks = createAsyncThunk(
  'stock/fetchNegativeStocks',
  async (_, { rejectWithValue }) => {
    try {
      return await stockService.getNegativeStocks();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        'Erreur lors de la récupération des stocks négatifs'
      );
    }
  }
);

const initialState = {
  stocks: [],
  totalCount: 0,
  selectedStock: null,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,

  inventoryRealQuantitiesJob: null,
  inventoryRealQuantitiesJobStatus: 'idle', // idle | pending | completed | failed
};

const stockSlice = createSlice({
  name: 'stock',
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedStock: (state, action) => {
      state.selectedStock = action.payload;
    },

    saveInventoryRealQuantitiesSocketSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      state.inventoryRealQuantitiesJobStatus = 'completed';

      if (
        state.inventoryRealQuantitiesJob?.jobId &&
        state.inventoryRealQuantitiesJob.jobId !== action.payload?.jobId
      ) {
        return;
      }

      const items =
        action.payload?.result?.items ||
        action.payload?.result?.Items ||
        [];

      if (Array.isArray(items) && items.length > 0) {
        state.stocks = items;
        state.totalCount = items.length;
      }
    },

    saveInventoryRealQuantitiesSocketFailed: (state, action) => {
      state.loading = false;
      state.inventoryRealQuantitiesJobStatus = 'failed';

      if (
        state.inventoryRealQuantitiesJob?.jobId &&
        action.payload?.jobId &&
        state.inventoryRealQuantitiesJob.jobId !== action.payload.jobId
      ) {
        return;
      }

      state.error = action.payload?.error || 'Une erreur est survenue.';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStocks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStocks.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchStocks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchActiveStocks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveStocks.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchActiveStocks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchStockById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStockById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedStock = action.payload;
      })
      .addCase(fetchStockById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchStocksByProductId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStocksByProductId.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(fetchStocksByProductId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchStocksByShopId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStocksByShopId.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchStocksByShopId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchExpiredStocks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExpiredStocks.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(fetchExpiredStocks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addStock.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks.push(action.payload);
        state.showModal = false;
      })
      .addCase(addStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addStockRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addStockRange.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addStockRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editStock.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.stocks.findIndex(stock => stock.stockId === action.payload.id);
        if (index !== -1) {
          state.stocks[index] = action.payload;
        }
        state.showModal = false;
      })
      .addCase(editStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeStock.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = state.stocks.filter(stock => stock.stockId !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(removeStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(hardDeleteStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(hardDeleteStock.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = state.stocks.filter(stock => stock.stockId !== action.payload);
      })
      .addCase(hardDeleteStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createStockLine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStockLine.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createStockLine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(processStockMovement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processStockMovement.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(processStockMovement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(validateStockAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateStockAvailability.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(validateStockAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchStocksByInvnetoryId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStocksByInvnetoryId.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchStocksByInvnetoryId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ICI: HTTP accepted seulement, pas succès métier final
      .addCase(saveInventoryRealQuantities.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.inventoryRealQuantitiesJobStatus = 'pending';
        state.inventoryRealQuantitiesJob = null;
      })
      .addCase(saveInventoryRealQuantities.fulfilled, (state, action) => {
        state.loading = true;
        state.error = null;
        state.inventoryRealQuantitiesJobStatus = 'pending';
        state.inventoryRealQuantitiesJob = action.payload;
      })
      .addCase(saveInventoryRealQuantities.rejected, (state, action) => {
        state.loading = false;
        state.inventoryRealQuantitiesJobStatus = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchTrashStocks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrashStocks.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchTrashStocks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchTransformStocks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransformStocks.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchTransformStocks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(markStockToDestroy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markStockToDestroy.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload;
        const index = state.stocks.findIndex(s => s.stockId === id);
        if (index !== -1) {
          state.stocks[index] = {
            ...state.stocks[index],
            toDestroy: true,
          };
        }
      })
      .addCase(markStockToDestroy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(markStockToDestroyWithQte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markStockToDestroyWithQte.fulfilled, (state, action) => {
        state.loading = false;
        const { id }  = action.payload;
        const index = state.stocks.findIndex(s => s.stockId ===  id);
      
        if (index !== -1) {
          state.stocks[index] = {
            ...state.stocks[index],
            toDestroy: true,
          };
        }
      })
      .addCase(markStockToDestroyWithQte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(restoreStockFromDestruction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreStockFromDestruction.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload;
        const index = state.stocks.findIndex(s => s.stockId === id);
        if (index !== -1) {
          state.stocks[index] = {
            ...state.stocks[index],
            toDestroy: false,
          };
        }
      })
      .addCase(restoreStockFromDestruction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(markStockToTransform.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markStockToTransform.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload;
        const index = state.stocks.findIndex(s => s.stockId === id);
        if (index !== -1) {
          state.stocks[index] = {
            ...state.stocks[index],
            toTransform: true,
          };
        }
      })
      .addCase(markStockToTransform.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateStockDateAndQuantity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStockDateAndQuantity.fulfilled, (state, action) => {
        state.loading = false;
        const { id, payload } = action.payload;
        const index = state.stocks.findIndex(s => s.stockId === id);
        if (index !== -1) {
          state.stocks[index] = {
            ...state.stocks[index],
            productionDate: payload.productionDate,
            theoricQuantity: payload.theoricQuantity,
          };
        }
      })
      .addCase(updateStockDateAndQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(transformStockProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transformStockProduct.fulfilled, (state, action) => {
        state.loading = false;
        const { id, request } = action.payload;
        const index = state.stocks.findIndex(s => s.stockId === id);
        if (index !== -1) {
          state.stocks[index] = {
            ...state.stocks[index],
            productId: request.newProductId,
            theoricQuantity: request.newTheoricQuantity,
            productionDate: request.productionDate ?? state.stocks[index].productionDate,
          };
        }
      })
      .addCase(transformStockProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchNegativeStocks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNegativeStocks.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || action.payload?.Items || []);
        state.totalCount =
          action.payload?.totalCount ||
          action.payload?.TotalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchNegativeStocks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedStock,
  saveInventoryRealQuantitiesSocketSuccess,
  saveInventoryRealQuantitiesSocketFailed,
} = stockSlice.actions;

export default stockSlice.reducer;